import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdPerson } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import CountrySelect from '../../../shared/layout/forms/CountrySelect';
import { SettingsContext } from '../../SettingsContext';
import PhoneNumberInput from '../../global/PhoneNumberInput';
import CitySearchSelect from '../../global/CitySearchSelect';
import { GetMe, updateMyProfile } from '../../../services/Http/Api';
import { useAuth } from '../../../context/AuthProvider';

const ProfilePage = () => {
    const { t } = useTranslation();
    const { siteSettings } = useContext(SettingsContext);
    const { user, refreshUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        phone_number: '',
        phone_country_id: '',
        country_id: '',
        city_id: '',
        city: '',
        address: '',
        gender: '',
        date_of_birth: '',
    });

    useEffect(() => {
        const load = async () => {
            const me = user || await GetMe();
            setFormData({
                name: me?.name || '',
                surname: me?.surname || '',
                email: me?.email || '',
                phone_number: me?.phone_number || '',
                phone_country_id: me?.phone_country_id || '',
                country_id: me?.country_id || '',
                city_id: me?.city_id || '',
                city: me?.city || '',
                address: me?.address || '',
                gender: me?.gender || '',
                date_of_birth: me?.date_of_birth ? me.date_of_birth.slice(0, 10) : '',
            });
        };
        load();
    }, [user]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = ({ phoneCountryCode, phone }) => {
        setFormData((prev) => ({
            ...prev,
            phone_country_id: phoneCountryCode,
            phone_number: phone,
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Always include required fields
            const payload = {
                name: formData.name,
                email: formData.email,
            };

            // Include optional fields only if they have a real value
            // Sending null for integer/exists fields can cause 422 in some Laravel versions
            const optionalStringFields = ['surname', 'city', 'address'];
            optionalStringFields.forEach((field) => {
                if (formData[field] !== null && formData[field] !== undefined) {
                    payload[field] = formData[field] || null;
                }
            });

            // Gender: only send if it's a value the backend accepts (Male or Female)
            const VALID_GENDERS = ['Male', 'Female'];
            if (VALID_GENDERS.includes(formData.gender)) {
                payload.gender = formData.gender;
            } else {
                payload.gender = null;  // Treat 'Other', '', null, or any invalid value as null
            }

            // Integer fields: only include if non-empty
            const integerFields = ['phone_country_id', 'country_id', 'city_id'];
            integerFields.forEach((field) => {
                const val = formData[field];
                if (val !== '' && val !== null && val !== undefined && val !== 0) {
                    payload[field] = parseInt(val, 10);
                }
            });

            // Date fields: only include if non-empty
            if (formData.date_of_birth) {
                payload.date_of_birth = formData.date_of_birth;
            }

            // Phone number: only include if non-empty
            if (formData.phone_number) {
                payload.phone_number = formData.phone_number;
            }

            await updateMyProfile(payload);
            if (refreshUser) {
                await refreshUser();
            }
            toast.success(t('Profile.saveSuccess', { defaultValue: 'Profile updated successfully.' }));
        } catch (error) {
            console.error('Failed to update profile:', error);
            toast.error(t('Profile.saveError', { defaultValue: 'Failed to update profile. Please try again.' }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdPerson size={28} />}>{t('Profile.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                <StyledCard>
                    <FormGrid>
                        <Field>
                            <Label>{t('Profile.name')}</Label>
                            <Input name="name" value={formData.name} onChange={handleInputChange} />
                        </Field>
                        <Field>
                            <Label>{t('Profile.surname')}</Label>
                            <Input name="surname" value={formData.surname} onChange={handleInputChange} />
                        </Field>
                        <Field>
                            <Label>{t('Profile.email')}</Label>
                            <Input name="email" value={formData.email} onChange={handleInputChange} disabled />
                        </Field>
                        <Field>
                            <Label>{t('Profile.country')}</Label>
                            <CountrySelect
                                countries={siteSettings?.countries || []}
                                value={formData.country_id}
                                onChange={(_, nextValue) => {
                                    const value = nextValue || '';
                                    handleInputChange({ target: { name: 'country_id', value } });
                                    setFormData((prev) => ({ ...prev, city: '', city_id: '' }));
                                }}
                                inputId="profile-country"
                                placeholder={t('Profile.selectCountry')}
                            />
                        </Field>
                        <Field>
                            <Label>{t('Profile.city')}</Label>
                            <CitySearchSelect
                                countryId={formData.country_id || null}
                                cityId={formData.city_id || null}
                                value={formData.city}
                                placeholder={t('Profile.city')}
                                onChange={(city) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        city: city?.name || '',
                                        city_id: city?.id || '',
                                    }))
                                }
                            />
                        </Field>
                        <Field>
                            <Label>{t('Profile.address')}</Label>
                            <Input name="address" value={formData.address} onChange={handleInputChange} />
                        </Field>
                        <Field>
                            <Label>{t('Profile.phone')}</Label>
                            <PhoneNumberInput
                                countryCodes={siteSettings?.countries || []}
                                phoneValue={formData.phone_number}
                                phoneCountryValue={String(formData.phone_country_id || '')}
                                onChange={handlePhoneChange}
                            />
                        </Field>
                        <Field>
                            <Label>{t('Profile.gender')}</Label>
                            <Select
                                value={formData.gender}
                                onChange={(event, nextValue) => handleInputChange({ target: { name: 'gender', value: nextValue ?? event?.target?.value ?? '' } })}
                                options={[
                                    { value: '', label: t('Profile.selectGender') },
                                    { value: 'Male', label: t('Profile.male') },
                                    { value: 'Female', label: t('Profile.female') },
                                ]}
                                inputId="profile-gender"
                            />
                        </Field>
                        <Field>
                            <Label>{t('Profile.dateOfBirth')}</Label>
                            <Input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleInputChange} />
                        </Field>
                    </FormGrid>
                    <Actions>
                        <MainButton onClick={handleSubmit} disabled={loading}>
                            {loading ? t('Profile.saving') : t('Profile.save')}
                        </MainButton>
                    </Actions>
                </StyledCard>
            </PageContent>
        </PageContainer>
    );
};

export default ProfilePage;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.25rem;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Label = styled.label`
    font-weight: 600;
    font-size: 0.875rem;
    color: #4b5563;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.75rem 0.875rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 0.95rem;
    background: #ffffff;
    transition: all 0.2s ease;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);

    &:focus {
        border-color: #821692;
        box-shadow: 0 0 0 4px rgba(130, 22, 146, 0.1);
        outline: none;
    }

    &:disabled {
        background-color: #f3f4f6;
        cursor: not-allowed;
    }
`;

const Select = styled(SimpleSelect)`
    width: 100%;
    font-size: 0.95rem;
`;

const Actions = styled.div`
    margin-top: 1.875rem;
    display: flex;
    justify-content: flex-end;
    padding-top: 1.25rem;
    border-top: 1px solid #f3f4f6;
`;
